import { isQuotationSparse } from '../quotationLayoutUtils';
import { hasSubPartyBreakdown } from './subPartyUtils';

const SORT_UNASSIGNED = '\uFFFE';

function isVisibleItem(row) {
  const hasDesc = !!(row.description && String(row.description).trim());
  const hasItem = !!(row.item && String(row.item).trim());
  const hasNumbers =
    (parseFloat(row.qty) || 0) > 0 ||
    (parseFloat(row.rate) || 0) > 0 ||
    (parseFloat(row.amount) || 0) > 0 ||
    (parseFloat(row.total) || 0) > 0;
  return hasDesc || hasItem || hasNumbers;
}

function getSubPartyNameForBillRowIndex(subPartyBreakdownList, rowIndex) {
  if (rowIndex == null || rowIndex < 0) return '';
  for (let i = 0; i < subPartyBreakdownList.length; i++) {
    const sp = subPartyBreakdownList[i];
    if (!sp || !Array.isArray(sp.lineIndices)) continue;
    if (sp.lineIndices.includes(rowIndex)) return String(sp.name || '').trim();
  }
  return '';
}

function calculateDiscount(items, billData) {
  const totalBeforeDiscount = items.reduce((sum, row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    return sum + qty * rate;
  }, 0);

  let discount = 0;
  if (billData.discountType && billData.discountValue) {
    if (billData.discountType === 'percent') {
      discount = totalBeforeDiscount * (parseFloat(billData.discountValue) || 0) / 100;
    } else {
      discount = parseFloat(billData.discountValue) || 0;
    }
  } else {
    discount = items.reduce((sum, row) => {
      const qty = parseFloat(row.qty) || 0;
      const rate = parseFloat(row.rate) || 0;
      const rawAmount = qty * rate;
      let lineDisc = 0;
      if ((row.lineDiscountType || 'amount') === 'percent') {
        lineDisc = rawAmount * ((parseFloat(row.lineDiscountValue) || 0) / 100);
      } else {
        lineDisc = parseFloat(row.lineDiscountValue) || 0;
      }
      lineDisc = Math.min(Math.max(lineDisc, 0), rawAmount);
      return sum + lineDisc;
    }, 0);
  }

  return { totalBeforeDiscount, discount, payableAmount: Math.max(0, totalBeforeDiscount - discount) };
}

export function useQuotationBillData(billData = {}, companyDetails = {}) {
  const items = billData.items || billData.rows || [];
  const subPartyBreakdownList = Array.isArray(billData.subPartyBreakdown) ? billData.subPartyBreakdown : [];

  const filteredIndexed = items
    .map((it, billRowIndex) => ({ row: it, billRowIndex }))
    .filter(({ row }) => isVisibleItem(row));

  const hasSubPartyColumn = hasSubPartyBreakdown(billData);

  const sortedIndexed = hasSubPartyColumn
    ? [...filteredIndexed].sort((a, b) => {
        const na = getSubPartyNameForBillRowIndex(subPartyBreakdownList, a.billRowIndex) || SORT_UNASSIGNED;
        const nb = getSubPartyNameForBillRowIndex(subPartyBreakdownList, b.billRowIndex) || SORT_UNASSIGNED;
        const c = na.localeCompare(nb, undefined, { sensitivity: 'base' });
        if (c !== 0) return c;
        return a.billRowIndex - b.billRowIndex;
      })
    : filteredIndexed;

  const displayItems = sortedIndexed.map(({ row, billRowIndex }) => ({
    ...row,
    _billRowIndex: billRowIndex,
    _subPartyName: hasSubPartyColumn
      ? getSubPartyNameForBillRowIndex(subPartyBreakdownList, billRowIndex)
      : '',
  }));

  const { totalBeforeDiscount, discount, payableAmount } = calculateDiscount(items, billData);
  const terms = billData.quotationTermsOverride || companyDetails.quotationTerms || '';
  const sparseLayout = isQuotationSparse(displayItems.length);
  const hasLineDiscount = items.some((row) => parseFloat(row.lineDiscountValue) > 0);
  const discountLabel =
    billData.discountType && billData.discountValue
      ? billData.discountType === 'percent'
        ? `Discount (${billData.discountValue}%)`
        : 'Discount'
      : 'Discount';

  return {
    items,
    displayItems,
    subPartyBreakdownList,
    totalBeforeDiscount,
    discount,
    payableAmount,
    terms,
    sparseLayout,
    hasLineDiscount,
    discountLabel,
    hasSubPartyColumn,
  };
}
